import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaUser, FaEnvelope, FaPhone, FaFileUpload, FaBriefcase } from "react-icons/fa";
import "./design/JobInquiry.css";
import { getApiUrl } from "../utils/api";
const pageVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100 } },
};

export default function JobInquiry() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    resume: null,
  });
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  // Fetch latest job openings
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(getApiUrl("get-job-openings.php"));
        const result = await response.json();
        if (result.status === "success") setJobs(result.data);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }
    };
    fetchJobs();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "resume") {
      const file = files[0];
      setFormData({ ...formData, resume: file });
      setFileName(file ? file.name : "");
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.contact || !formData.resume || !selectedJob) {
      alert("Please fill all fields and select a job!");
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("contact", formData.contact);
    data.append("resume", formData.resume);
    data.append("job_id", selectedJob);

    // Debug FormData contents
    for (let [key, value] of data.entries()) {
      console.log(key, value);
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl("submit-jobinquiry.php"), {
        method: "POST",
        body: data,
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSuccess(true);
        setFormData({ name: "", email: "", contact: "", resume: null });
        setFileName("");
        setSelectedJob(null);
        e.target.reset();
      } else {
        alert(result.message || "Something went wrong!");
      }
    } catch (error) {
      console.error(error);
      alert("Error submitting application");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div className="job-inquiry-page" variants={pageVariants} initial="hidden" animate="visible">
      {/* Job Openings Section */}
      <motion.section className="openings-section" variants={itemVariants}>
        <h2 className="openings-title"><FaBriefcase /> Current Openings</h2>
        <div className="job-cards">
          {jobs.length > 0 ? (
            jobs.map((job) => (
              <motion.div
                key={job.id}
                className={`job-card ${selectedJob === job.id ? "selected" : ""}`}
                onClick={() => setSelectedJob(job.id)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <h3>{job.position_name}</h3>
                <p><strong>Total Openings:</strong> {job.total_openings}</p>
                <p>{job.job_description}</p>
                <p><em>{job.location}</em></p>
              </motion.div>
            ))
          ) : (
            <p id="no-current">No current openings available.</p>
          )}
        </div>
      </motion.section>

      {/* Job Inquiry Form */}
      <motion.div className="job-inquiry-container" variants={itemVariants}>
        <h2 className="form-title">Apply Now</h2>
        <form className="job-inquiry-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label><FaUser /> Full Name</label>
            <input name="name" type="text" value={formData.name} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label><FaEnvelope /> Email</label>
            <input name="email" type="email" value={formData.email} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label><FaPhone /> Contact</label>
            <input name="contact" type="tel" value={formData.contact} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label><FaFileUpload /> Resume</label>
            <input name="resume" type="file" accept=".pdf,.doc,.docx" onChange={handleChange} required />
            <span>{fileName}</span>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? "Submitting..." : "Submit Application"}
          </button>

          {success && <p className="success-message">Application submitted successfully!</p>}
        </form>
      </motion.div>
    </motion.div>
  );
}
